/**
 * 경로 상수 SSoT — 문자열 경로를 컴포넌트에 흩뿌리지 않는다.
 * FO(공개)와 워크스페이스(CONSULTANT)를 한 라우터에서 서빙하며, 워크스페이스는 `/workspace` prefix 고정
 * (운영 Nginx 경로 분리 규약과 동일 URL).
 */
export const WORKSPACE_BASE = '/workspace';
/** 모바일 화면 접두(specs/037) — `/workspace` 하위라 세션 가드·로그인 복귀·nginx SPA 경로를 그대로 쓴다. */
const MOBILE_BASE = `${WORKSPACE_BASE}/m`;

/** 공고 상세 "지원 가능한 내 고객사" 섹션 앵커 — 링크와 섹션 id 가 같은 값을 쓴다. */
export const ANNOUNCEMENT_CANDIDATES_ANCHOR = 'candidates';

export const ROUTES = {
  home: '/',
  login: '/login',
  signup: '/signup',
  signupVerifyEmail: '/signup/verify-email',
  verifyEmail: '/verify-email',
  verifyEmailPending: '/verify-email/pending',
  passwordForgot: '/password/forgot',
  passwordReset: '/password/reset',
  authCallback: '/auth/callback',
  errorAuthRequired: '/error/auth-required',
  errorEmailConflict: '/error/email-conflict',
  errorVerify: '/error/verify',
  /** 메일 수신거부(specs/080) — 백엔드 메일 링크와 같은 경로 */
  notificationUnsubscribe: '/notification/unsubscribe',
  /** 공개 화면(specs/070) — 3문항 체험·공고 둘러보기·올해 꼭 보는 사업·기업 인증 안내 */
  trial: '/trial',
  /** 예비창업 자격 진단(specs/072) */
  diagnosis: '/diagnosis',
  programs: '/programs',
  program: (id: number | string) => `/programs/${id}`,
  featured: '/featured',
  certifications: '/certifications',
  certification: (slug: string) => `/certifications/${slug}`,
  legal: {
    terms: '/legal/terms',
    refund: '/legal/refund',
    privacy: '/legal/privacy',
    business: '/legal/business',
  },
  workspace: {
    root: WORKSPACE_BASE,
    /** 구 AI 검색 주소(specs/030) — 진입 시 공고 찾기·업체 "맞는 공고"·공고 상세로 이어진다(specs/031 FR-005). */
    legacySearch: `${WORKSPACE_BASE}/search`,
    /** 기업 회원 홈·우리 회사·입력 방식 온보딩(specs/065) */
    home: `${WORKSPACE_BASE}/home`,
    myCompany: `${WORKSPACE_BASE}/my-company`,
    myCompanyOnboarding: `${WORKSPACE_BASE}/my-company/onboarding`,
    /** 기업 공고 찾기 — 우리 회사 기준 판정 목록(specs/069) */
    myAnnouncements: `${WORKSPACE_BASE}/my-announcements`,
    /** 기업 성장 로드맵 — 우리 회사 기준(specs/071) */
    myRoadmap: `${WORKSPACE_BASE}/my-roadmap`,
    dashboard: `${WORKSPACE_BASE}/dashboard`,
    calendar: `${WORKSPACE_BASE}/calendar`,
    companies: `${WORKSPACE_BASE}/companies`,
    companyNew: `${WORKSPACE_BASE}/companies/new`,
    /** 업체 일괄 등록(specs/034) */
    companyBulk: `${WORKSPACE_BASE}/companies/bulk`,
    company: (id: number | string) => `${WORKSPACE_BASE}/companies/${id}`,
    /** 업체 상세 "맞는 공고" 탭(고객사 기준 검색, specs/031 FR-040) — `query` 는 추가 질의 초기값. */
    companyMatch: (id: number | string, query?: string) =>
      `${WORKSPACE_BASE}/companies/${id}?${new URLSearchParams(query ? { tab: 'match', q: query } : { tab: 'match' })}`,
    program: (id: number | string) => `${WORKSPACE_BASE}/programs/${id}`,
    /** 공고 찾기 — 워크스페이스 첫 화면(specs/031 FR-006). 검색어 없음 = 목록 모드, 있음 = 검색 모드. */
    announcements: `${WORKSPACE_BASE}/announcements`,
    announcement: (id: number | string) => `${WORKSPACE_BASE}/announcements/${id}`,
    /** 공고 상세의 "지원 가능한 내 고객사" 섹션(지원사업 기준, specs/031 FR-041). */
    /** `search` = 공고 찾기 화면 query — 상세의 [공고 찾기로]가 같은 검색·필터로 돌아오게 해시 앞에 보존한다. */
    announcementCandidates: (id: number | string, search?: string) =>
      `${WORKSPACE_BASE}/announcements/${id}${search ? `?${search}` : ''}#${ANNOUNCEMENT_CANDIDATES_ANCHOR}`,
    consultantAnnouncements: `${WORKSPACE_BASE}/consultant-announcements`,
    consultantAnnouncement: (id: number | string) =>
      `${WORKSPACE_BASE}/consultant-announcements/${id}`,
    /** 사용 안내 영상(specs/035) */
    guide: `${WORKSPACE_BASE}/guide`,
    certifications: `${WORKSPACE_BASE}/certifications`,
    certification: (slug: string) => `${WORKSPACE_BASE}/certifications/${slug}`,
    subscription: `${WORKSPACE_BASE}/subscription`,
    checkout: `${WORKSPACE_BASE}/subscription/checkout`,
    billingCallback: `${WORKSPACE_BASE}/subscription/billing-callback`,
    /** 모바일 화면(specs/037) — 현장용 등록·조회·공고 검색만. 그 밖의 경로는 PC 안내 화면. */
    mobile: {
      root: MOBILE_BASE,
      /** 기업 회원(specs/065) */
      home: `${MOBILE_BASE}/home`,
      myCompany: `${MOBILE_BASE}/my-company`,
      myCompanyOnboarding: `${MOBILE_BASE}/my-company/onboarding`,
      myAnnouncements: `${MOBILE_BASE}/my-announcements`,
      myRoadmap: `${MOBILE_BASE}/my-roadmap`,
      certifications: `${MOBILE_BASE}/certifications`,
      certification: (slug: string) => `${MOBILE_BASE}/certifications/${slug}`,
      companies: `${MOBILE_BASE}/companies`,
      companyNew: `${MOBILE_BASE}/companies/new`,
      company: (id: number | string) => `${MOBILE_BASE}/companies/${id}`,
      companyMatch: (id: number | string) => `${MOBILE_BASE}/companies/${id}/match`,
      search: `${MOBILE_BASE}/search`,
      announcement: (id: number | string) => `${MOBILE_BASE}/announcements/${id}`,
      /** 사업 상세(specs/092) */
      program: (id: number | string) => `${MOBILE_BASE}/programs/${id}`,
      /** 컨설턴트 공고(specs/040) */
      consultantAnnouncements: `${MOBILE_BASE}/consultant-announcements`,
      consultantAnnouncement: (id: number | string) =>
        `${MOBILE_BASE}/consultant-announcements/${id}`,
    },
  },
} as const;
